import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Lock, Bot, Image, Key, Zap, Globe, FileText, HelpCircle,
  Files, ShoppingBag, ListOrdered, Sparkles, Infinity, Star, Pencil, Facebook,
  PenTool, Link, Settings, ListFilter, CheckCircle, Share, Users,
} from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';

const headingStyles = {
  fontFamily: "'Manrope', sans-serif",
};

const bodyStyles = {
  fontFamily: "'Poppins', sans-serif",
};

const FeatureGrid = ({ handleLogin }) => {
  const features = [
    {
      icon: FileText,
      title: "Article Generator",
      description: "Transform basic ideas into fully-formed articles with customizable sections, headings, and keywords. No writing experience needed."
    },
    {
      icon: Image,
      title: "Image Generator",
      description: "Create professional images with simple text prompts. Perfect for featured images, social media, or product visuals. No design skills required."
    },
    {
      icon: Bot,
      title: "Content Rewriter",
      description: "Take existing articles and transform them into fresh, unique content that matches your brand voice while avoiding plagiarism concerns."
    },
    {
      icon: Link,
      title: "Amazon Product Reviews",
      description: "Generate detailed product reviews and comparison articles with automatic product data and images from Amazon links - ideal for affiliate marketing."
    },
    {
      icon: Settings,
      title: "Simple Settings",
      description: "Choose from budget-friendly to premium AI options with our easy-to-understand settings. Control costs with clear pricing for every feature."
    },
    {
      icon: Sparkles,
      title: "AI Enhancement",
      description: "Our AI works behind the scenes to improve your content, from optimizing image prompts to enhancing SEO without you needing AI expertise."
    },
    {
      icon: ListFilter,
      title: "Prompt Lab",
      description: "A beginner-friendly way to create and save templates for consistent content. No prompt engineering knowledge required."
    },
    {
      icon: Facebook,
      title: "Social Media Tools",
      description: "Create Facebook-ready content and visuals that drive engagement, with templates specifically designed for social media success."
    },
    {
      icon: PenTool,
      title: "Content Control",
      description: "Set your preferred article length, structure, and style. Perfect for maintaining consistent content across your website or blog."
    }
  ];

  return (
    <section id="features-section" className="py-24 bg-gradient-to-b from-white to-orange-50/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
            All The Tools For{" "}
            <span className="bg-gradient-to-r from-orange-600 to-orange-500 text-transparent bg-clip-text">
              Effortless Content
            </span>
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            Focus on your ideas while our platform handles the technical work of content creation
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div 
              key={index}
              className="group p-8 bg-white rounded-2xl border border-gray-100 hover:border-orange-200 hover:shadow-xl transition-all duration-300 relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-2.5 bg-orange-50 rounded-xl group-hover:bg-orange-100 transition-colors duration-300">
                    <feature.icon className="w-6 h-6 text-orange-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 group-hover:text-orange-600 transition-colors duration-300">
                    {feature.title}
                  </h3>
                </div>
                <p className="text-gray-600 leading-relaxed group-hover:text-gray-700 transition-colors duration-300">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-center mt-12 mb-4">
          <button
            onClick={handleLogin}
            className="px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl hover:from-orange-600 hover:to-orange-700 font-semibold flex items-center gap-2 shadow-lg transition-all transform hover:-translate-y-0.5"
          >
            <Zap className="w-5 h-5" />
            Get Scripti Now
          </button>
        </div>
      </div>
    </section>
  );
};

const LandingPage = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const { loginWithRedirect } = useAuth0();

  const handleLogin = () => {
    localStorage.clear();
    sessionStorage.clear();
    loginWithRedirect({
      prompt: 'login',
      appState: { returnTo: '/articles/standard' }
    });
  };

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  const faqs = [
    {
      q: "What's included in the subscription price?",
      a: "Your $27/month subscription gives you full access to all Scripti features - article creation, image generation, templates, WordPress integration, and more. You only pay extra for the AI services you use (OpenAI, Anthropic, and Fal.ai) which typically costs $5-15/month for most users."
    },
    {
      q: "How do the API costs work?",
      a: "You connect your own OpenAI, Anthropic, or Fal.ai accounts. Then you only pay for what you use. For most users, generating a 1,000-word article costs between $0.001 (budget models) to $0.024 (premium models). Images typically cost $0.02-$0.10 each depending on quality."
    },
    {
      q: "Do I need technical knowledge to use Scripti?",
      a: "Not at all! We've designed Scripti to be user-friendly for complete beginners. You'll just need to create accounts with the AI providers and copy/paste your API keys. We provide step-by-step guides to help you get set up."
    },
    {
      q: "What happens when I sign up?",
      a: "When you sign up for a subscription, you'll immediately gain access to all platform features. You'll need to connect your own API keys to start creating content. We provide detailed setup guides to help you get started quickly."
    },
    {
      q: "What if I'm not satisfied?",
      a: "You can cancel your subscription at any time with no additional fees. We do not offer refunds for partial monthly usage as you gain immediate access to all features. (EU/UK residents: 14-day cooling off refund period applies as per regulations)"
    },
    {
      q: "How much content can I create?",
      a: "There are no limits on the amount of content you can create! Using budget-friendly models like GPT-4o mini, you could create 10,000+ articles for just $10 of API costs. The only limit is how much you want to spend on the APIs."
    },
    {
      q: "Do I need to be an AI prompt expert?",
      a: "Not at all. Scripti is designed to handle the complex prompting for you. Our user-friendly interface lets you create content without needing to understand prompt engineering or AI terminology."
    },
    {
      q: "What is OpenRouter and do I need it?",
      a: "OpenRouter is an optional service that gives you access to more AI models through a single account. It's not required - you can use just OpenAI or Anthropic directly if you prefer. OpenRouter is recommended if you want easy access to a wider variety of models."
    },
    {
      q: "Can I publish directly to my website?",
      a: "Yes! We offer one-click publishing to WordPress sites. You can send both your content and AI-generated images directly to your WordPress media library and post editor."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 antialiased" style={headingStyles}>
      {/* Navigation */}
      <nav className="fixed w-full bg-white/90 backdrop-blur-lg shadow-sm z-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-2">
              <img 
                src="/Scriptilogo.jpg" 
                alt="Scripti Logo" 
                className="h-12 w-auto" 
              />
            </div>
            
            {/* Desktop Links */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features-section" className="text-gray-700 font-medium hover:text-orange-600 transition-colors">Features</a>
              <a href="#pricing" className="text-gray-700 font-medium hover:text-orange-600 transition-colors">Pricing</a>
              <a href="#faq" className="text-gray-700 font-medium hover:text-orange-600 transition-colors">FAQ</a>
              <button
                onClick={handleLogin}
                className="px-5 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-all font-medium shadow-sm hover:shadow-md"
              >
                Get Started
              </button>
            </div>

            <button 
              className="md:hidden p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              <svg className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>

          {/* Mobile Links */}
          {isMobileMenuOpen && (
            <div className="md:hidden pb-6 px-2 bg-white/95 backdrop-blur-lg">
              <a href="#features-section" className="block py-3 text-gray-700 font-medium hover:text-orange-600 transition-colors">Features</a>
              <a href="#pricing" className="block py-3 text-gray-700 font-medium hover:text-orange-600 transition-colors">Pricing</a>
              <a href="#faq" className="block py-3 text-gray-700 font-medium hover:text-orange-600 transition-colors">FAQ</a>
              <button
                onClick={handleLogin}
                className="w-full mt-3 px-4 py-2.5 bg-orange-600 text-white rounded-lg font-medium"
              >
                Get Started
              </button>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50 via-white to-orange-50">
          <div className="absolute inset-0 bg-[url('/grid.svg')] opacity-10"></div>
        </div>
        <div className="relative max-w-7xl mx-auto">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-extrabold mt-12 text-gray-900 mb-6 leading-tight tracking-tight">
              AI Content Creation
              <br />
              <span className="bg-gradient-to-r from-orange-600 to-orange-500 text-transparent bg-clip-text">
                Made Simple & Flexible
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
              Create professional articles, generate stunning images, and customize every aspect of your content with full prompt control
            </p>
            <p className="text-md mb-10 max-w-3xl mx-auto text-gray-500">
              Choose between quick-start tools for beginners or advanced prompt engineering for complete customization
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4 items-center">
              <button
                onClick={handleLogin}
                className="px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl hover:from-orange-600 hover:to-orange-700 font-semibold flex items-center gap-2 shadow-lg transition-all transform hover:-translate-y-0.5 w-full sm:w-auto"
              >
                <Zap className="w-5 h-5" />
                Get Started Now
              </button>
              <a 
                href="#features-section"
                className="px-8 py-4 bg-white text-gray-700 rounded-xl hover:bg-gray-50 font-semibold flex items-center gap-2 shadow-md border border-gray-200 transition-all w-full sm:w-auto justify-center"
              >
                <HelpCircle className="w-5 h-5" />
                See How It Works
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Grid */}
      <FeatureGrid handleLogin={handleLogin} />

      {/* Image Generation Section */}
      <section id="image-generation" className="py-24 bg-gradient-to-b from-white to-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
              <span className="bg-gradient-to-r from-orange-600 to-orange-500 text-transparent bg-clip-text">
                Professional Images
              </span>{" "}
              Without The Cost
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed mb-4">
              Generate beautiful, unique images for your content without expensive stock photo subscriptions or graphic designers
            </p>
            <p className="text-md text-gray-500">
              Simply describe what you want, and our AI creates it in seconds. Each image costs pennies, not dollars.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {/* Section 1: Content Creation Integration */}
            <div className="group bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-orange-50 rounded-xl group-hover:bg-orange-100 transition-colors duration-300">
                  <Image className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">For Your Articles</h3>
              </div>
              <ul className="space-y-4">
                <li className="flex items-center gap-3 text-gray-600 group-hover:text-gray-700 transition-colors">
                  <CheckCircle className="w-5 h-5 text-orange-600 flex-shrink-0" />
                  <span>Automatically create images for each section of your article</span>
                </li>
                <li className="flex items-center gap-3 text-gray-600 group-hover:text-gray-700 transition-colors">
                  <CheckCircle className="w-5 h-5 text-orange-600 flex-shrink-0" />
                  <span>Generate custom featured images that match your content</span>
                </li>
                <li className="flex items-center gap-3 text-gray-600 group-hover:text-gray-700 transition-colors">
                  <CheckCircle className="w-5 h-5 text-orange-600 flex-shrink-0" />
                  <span>Save hours of searching for the perfect stock photo</span>
                </li>
              </ul>
            </div>

            {/* Section 2: Standalone Image Generation */}
            <div className="group bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-orange-50 rounded-xl group-hover:bg-orange-100 transition-colors duration-300">
                  <Globe className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">Bulk Creation</h3>
              </div>
              <ul className="space-y-4">
                <li className="flex items-center gap-3 text-gray-600 group-hover:text-gray-700 transition-colors">
                  <CheckCircle className="w-5 h-5 text-orange-600 flex-shrink-0" />
                  <span>Create multiple images at once for social media posts</span>
                </li>
                <li className="flex items-center gap-3 text-gray-600 group-hover:text-gray-700 transition-colors">
                  <CheckCircle className="w-5 h-5 text-orange-600 flex-shrink-0" />
                  <span>Download in various formats for different platforms</span>
                </li>
                <li className="flex items-center gap-3 text-gray-600 group-hover:text-gray-700 transition-colors">
                  <CheckCircle className="w-5 h-5 text-orange-600 flex-shrink-0" />
                  <span>Direct upload to WordPress media library</span>
                </li>
              </ul>
            </div>

            {/* Section 3: Customization & Output */}
            <div className="group bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-orange-50 rounded-xl group-hover:bg-orange-100 transition-colors duration-300">
                  <Settings className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">Easy Customization</h3>
              </div>
              <ul className="space-y-4">
                <li className="flex items-center gap-3 text-gray-600 group-hover:text-gray-700 transition-colors">
                  <CheckCircle className="w-5 h-5 text-orange-600 flex-shrink-0" />
                  <span>Choose from different AI models based on your budget</span>
                </li>
                <li className="flex items-center gap-3 text-gray-600 group-hover:text-gray-700 transition-colors">
                  <CheckCircle className="w-5 h-5 text-orange-600 flex-shrink-0" />
                  <span>We help improve your prompts for better image quality</span>
                </li>
                <li className="flex items-center gap-3 text-gray-600 group-hover:text-gray-700 transition-colors">
                  <CheckCircle className="w-5 h-5 text-orange-600 flex-shrink-0" />
                  <span>Select different styles, sizes, and aspect ratios</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="flex justify-center mb-12">
            <button
              onClick={handleLogin}
              className="px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl hover:from-orange-600 hover:to-orange-700 font-semibold flex items-center gap-2 shadow-lg transition-all transform hover:-translate-y-0.5"
            >
              <Image className="w-5 h-5" />
              Get Scripti Now
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            <img 
              src="/Images/lion.png" 
              alt="AI Generated Lion" 
              className="w-full aspect-square object-cover rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
            />
            <img 
              src="/Images/manonstreet.png" 
              alt="AI Generated Man on Street" 
              className="w-full aspect-square object-cover rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
            />
            <img 
              src="/Images/newyork.png" 
              alt="AI Generated New York Scene" 
              className="w-full aspect-square object-cover rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
            />
            <img 
              src="/Images/womanchef.png" 
              alt="AI Generated Woman Chef" 
              className="w-full aspect-square object-cover rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
            />
            <img 
              src="/Images/womaninforest.png" 
              alt="AI Generated Woman in Forest" 
              className="w-full aspect-square object-cover rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
            />
            <img 
              src="/Images/eagle.png" 
              alt="AI Generated Eagle" 
              className="w-full aspect-square object-cover rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
            />
          </div>
        </div>
      </section>

      {/* Prompt Lab Section */}
      <section id="prompt-lab" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-4xl font-bold mb-6">
              Complete Control With
              <span className="text-orange-600"> Custom Prompts</span>
            </h2>
            <p className="text-lg text-gray-600 mb-4">
              Unlike other platforms that restrict you to pre-built templates, Scripti gives you the power to create any prompt you can imagine.
            </p>
            <p className="text-md text-gray-500">
              Whether you're a beginner or prompt engineering expert, you have full control over how your content is created.
            </p>
          </div>
      
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {/* Quick Start */}
            <div className="bg-gray-50 rounded-xl p-8">
              <div className="flex items-center gap-3 mb-4">
                <Zap className="w-6 h-6 text-orange-600" />
                <h3 className="text-xl font-semibold">Quick Start Articles</h3>
              </div>
              <p className="text-gray-600 mb-4">
                For beginners or those in a hurry, our simple article generator lets you create content with minimal input. Perfect for getting started quickly.
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Enter a topic and get content fast
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Pre-optimized for readability and SEO
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  No prompt engineering required
                </li>
              </ul>
            </div>
      
            {/* Advanced Prompt Building */}
            <div className="bg-gray-50 rounded-xl p-8">
              <div className="flex items-center gap-3 mb-4">
                <PenTool className="w-6 h-6 text-orange-600" />
                <h3 className="text-xl font-semibold">Advanced Prompt Control</h3>
              </div>
              <p className="text-gray-600 mb-4">
                For experts and those who want complete control, build custom prompts with exactly the instructions you want. No limits on your creativity.
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Full access to prompt engineering
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Create prompts for any use case
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  No restrictions on prompt length or complexity
                </li>
              </ul>
            </div>
      
            {/* Reusable Library */}
            <div className="bg-gray-50 rounded-xl p-8">
              <div className="flex items-center gap-3 mb-4">
                <FileText className="w-6 h-6 text-orange-600" />
                <h3 className="text-xl font-semibold">Prompt Library</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Save your favorite prompts for reuse. Build a library of prompts that work for your specific content needs and writing style.
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Build a personal prompt collection
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Organize prompts by category
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Iterate and improve over time
                </li>
              </ul>
            </div>
          </div>
      
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 shadow-sm">
            <h3 className="text-2xl font-bold mb-6">The Power of Unlimited Prompts</h3>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <p className="text-gray-600 mb-4">
                  With Scripti's unlimited prompt customization, you can:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <Star className="w-5 h-5 text-orange-600 mt-1" />
                    <span className="text-gray-600">Create content in any tone, style, or format you can imagine</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Pencil className="w-5 h-5 text-orange-600 mt-1" />
                    <span className="text-gray-600">Develop a unique brand voice that sets you apart</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Share className="w-5 h-5 text-orange-600 mt-1" />
                    <span className="text-gray-600">Experiment with different approaches to find what works best</span>
                  </li>
                </ul>
              </div>
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-gray-600">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span>Create highly specialized content for niche audiences</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span>Control every aspect of your content generation</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span>Apply advanced prompt techniques for better results</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span>Save successful prompts for consistent quality</span>
                </div>
              </div>
            </div>
            <div className="flex justify-center mt-8">
              <button
                onClick={handleLogin}
                className="px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl hover:from-orange-600 hover:to-orange-700 font-semibold flex items-center gap-2 shadow-lg transition-all transform hover:-translate-y-0.5"
              >
                <PenTool className="w-5 h-5" />
                Get Scripti Now
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Social Media Tools Section */}
      <section id="social-media-tools" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                <span className="text-orange-600">Social Media</span> Content Creator
              </h2>
              <p className="text-gray-600 mb-6">
                Create engaging posts, captions, and images tailored specifically for social media platforms. Perfect for keeping your social accounts active with minimal effort.
              </p>
              <ul className="space-y-4 text-gray-600">
                <li className="flex items-start gap-3">
                  <Facebook className="w-5 h-5 text-orange-600 mt-1" />
                  <div>
                    <h4 className="font-semibold">Facebook-Ready Content</h4>
                    <p className="text-sm">Generate posts optimized for Facebook's algorithm and audience expectations</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Image className="w-5 h-5 text-orange-600 mt-1" />
                  <div>
                    <h4 className="font-semibold">Social Media Visuals</h4>
                    <p className="text-sm">Create images in the right dimensions for each platform with eye-catching designs</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-orange-600 mt-1" />
                  <div>
                    <h4 className="font-semibold">Content Calendar Ideas</h4>
                    <p className="text-sm">Generate weeks of post ideas based on your business, audience, and goals</p>
                  </div>
                </li>
              </ul>
              <button
                onClick={handleLogin}
                className="mt-6 px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all font-medium shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
              >
                Get Scripti Now
              </button>
            </div>
            <div className="bg-white h-96 rounded-lg flex items-center justify-center overflow-hidden">
              <img 
                src="/FacebookScripti.png" 
                alt="Social Media Tools Interface" 
                className="w-full h-full object-cover rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50 via-white to-orange-50">
          <div className="absolute inset-0 bg-[url('/grid.svg')] opacity-10"></div>
        </div>
        <div className="relative max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
              Transparent Pricing,{" "}
              <span className="bg-gradient-to-r from-orange-600 to-orange-500 text-transparent bg-clip-text">
                No Surprises
              </span>
            </h2>
            <p className="text-xl text-gray-600 mb-4">
              One simple subscription for all Scripti features and tools
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 border border-gray-100 relative overflow-hidden">
            <div className="absolute top-0 right-0 -mt-10 -mr-10 w-40 h-40 bg-gradient-to-br from-orange-500/10 to-orange-600/10 blur-3xl rounded-full"></div>
            
            {/* Special Launch Price Badge */}
            <div className="absolute top-4 right-4 md:top-6 md:right-6">
              <div className="relative">
                <div className="absolute inset-0 opacity-70 blur-sm bg-orange-600 rounded-full transform scale-110"></div>
                <div className="relative py-1.5 px-4 bg-orange-600 text-white text-sm font-bold rounded-full">
                  Special Launch Price
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="flex flex-col items-center justify-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Platform Subscription</h3>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-7xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-orange-500">$17</span>
                  <span className="text-xl text-gray-600">/month</span>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="line-through text-gray-400 text-lg">$27</span>
                  <span className="text-orange-600 font-medium">Save $10 every month</span>
                </div>
              </div>

              <p className="text-gray-600 text-lg mb-8 text-center">
                Full access to all Scripti features and tools, with no hidden fees
              </p>
              
              <div className="grid md:grid-cols-2 gap-8 mb-12">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Infinity className="w-5 h-5 text-orange-600 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900">No Usage Limits</h4>
                      <p className="text-gray-600 text-sm">Create as much content as you need with no artificial caps</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Image className="w-5 h-5 text-orange-600 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900">All Content Types</h4>
                      <p className="text-gray-600 text-sm">Articles, blog posts, product reviews, images, and more</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Bot className="w-5 h-5 text-orange-600 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900">AI Model Selection</h4>
                      <p className="text-gray-600 text-sm">Choose budget models or premium quality based on your needs</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <PenTool className="w-5 h-5 text-orange-600 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Template Library</h4>
                      <p className="text-gray-600 text-sm">Create and save unlimited content templates</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Globe className="w-5 h-5 text-orange-600 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900">WordPress Integration</h4>
                      <p className="text-gray-600 text-sm">Publish directly to your WordPress site</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Key className="w-5 h-5 text-orange-600 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Your Own API Keys</h4>
                      <p className="text-gray-600 text-sm">Complete cost control and transparency</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Lock className="w-5 h-5 text-orange-600 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Cancel Anytime</h4>
                      <p className="text-gray-600 text-sm">No long-term contracts or commitments</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <HelpCircle className="w-5 h-5 text-orange-600 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Friendly Support</h4>
                      <p className="text-gray-600 text-sm">Get help in our private Facebook community</p>
                    </div>
                  </div>
                </div>
              </div>

              <button
                onClick={handleLogin}
                className="w-full px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl hover:from-orange-600 hover:to-orange-700 font-semibold transition-all shadow-md hover:shadow-xl transform hover:-translate-y-0.5 text-lg"
              >
                Get Scripti Now
              </button>
              <p className="text-center text-gray-500 text-sm mt-4">Limited time offer - regular price $27/month</p>
              <p className="text-center text-gray-500 text-xs mt-3">Additional API costs apply for OpenAI, Anthropic, and Fal.ai services</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Scripti Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-6 text-gray-900">
            Why Choose <span className="text-orange-600">Scripti</span>
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
            The perfect balance of ease-of-use for beginners and unlimited flexibility for advanced users
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-all text-center">
              <div className="flex flex-col items-center justify-center gap-3 mb-4">
                <div className="p-2 bg-orange-50 rounded-lg">
                  <PenTool className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Unlimited Prompt Control</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                Create any type of prompt you want with no restrictions. You're not limited to templates - build exactly what you need for your content.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-all text-center">
              <div className="flex flex-col items-center justify-center gap-3 mb-4">
                <div className="p-2 bg-orange-50 rounded-lg">
                  <Image className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Reduce Content Costs</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                Create content at a fraction of the cost of hiring freelancers. Generate articles for pennies instead of paying $50-$200 per article.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-all text-center">
              <div className="flex flex-col items-center justify-center gap-3 mb-4">
                <div className="p-2 bg-orange-50 rounded-lg">
                  <Bot className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">No AI Expertise Needed</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                Our simple interface handles all the complex AI prompting behind the scenes. You just answer a few questions about your topic and we do the rest.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-all text-center">
              <div className="flex flex-col items-center justify-center gap-3 mb-4">
                <div className="p-2 bg-orange-50 rounded-lg">
                  <Globe className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Works Anywhere, Anytime</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                Create content from any device with a web browser. No software to install or complex setups - just log in and start creating.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-all text-center">
              <div className="flex flex-col items-center justify-center gap-3 mb-4">
                <div className="p-2 bg-orange-50 rounded-lg">
                  <FileText className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Publish With One Click</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                Send finished content directly to WordPress with proper formatting, images, and SEO elements already in place. No more copy-and-paste hassles.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-all text-center">
              <div className="flex flex-col items-center justify-center gap-3 mb-4">
                <div className="p-2 bg-orange-50 rounded-lg">
                  <Sparkles className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Scale Your Content Easily</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                Create consistent, high-quality content at scale. Perfect for building content-rich websites, blogs, or e-commerce stores without hiring a team.
              </p>
            </div>
          </div>
          
          <div className="flex justify-center mt-12">
            <button
              onClick={handleLogin}
              className="px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl hover:from-orange-600 hover:to-orange-700 font-semibold flex items-center gap-2 shadow-lg transition-all transform hover:-translate-y-0.5"
            >
              <Sparkles className="w-5 h-5" />
              Get Scripti Now
            </button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-6 text-gray-900">Frequently Asked Questions</h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Everything you need to know about getting started with Scripti
          </p>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-all"
              >
                <button
                  className="w-full px-6 py-4 text-left hover:bg-gray-50 flex justify-between items-center"
                  onClick={() => toggleFaq(index)}
                >
                  <h3 className="text-lg font-semibold text-gray-900">{faq.q}</h3>
                  <svg
                    className={`w-5 h-5 text-gray-500 transform transition-transform ${
                      expandedFaq === index ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div
                  className={`px-6 py-4 bg-gray-50 ${
                    expandedFaq === index ? 'block' : 'hidden'
                  }`}
                >
                  <p className="text-gray-600">{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-12 pt-8 border-t border-gray-200 text-center text-gray-500 text-sm space-y-2">
            <p>* API pricing subject to change by providers (OpenAI, Anthropic, Fal.ai)</p>
            <p>* Content quality varies based on the AI models you choose to use</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-orange-600 py-12 px-4 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-2">
              <Sparkles size={28} color="#FFFFFF" />
              <span className="text-2xl font-extrabold">Scripti</span>
            </div>
            <div className="flex gap-6">
              <a href="/privacy.html" className="hover:text-orange-200 transition-colors">
                Privacy Policy
              </a>
              <a href="/terms.html" className="hover:text-orange-200 transition-colors">
                Terms & Conditions
              </a>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-orange-500 text-center">
            <p className="text-orange-100">© 2025 Scripti. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;